import React, { useCallback, useEffect, useState } from 'react';
import { api } from '../utils/api';
import { todayEastern, toEasternTime } from '../utils/time';
import { useListFilter } from '../hooks/useListFilter';
import Tabs from '../components/Tabs';
import { PageHeader, StatCard, Panel, StatusPill, Empty, Skeleton, Icon, SortTh, useSort, usePager, Pager, useToast } from '../../components/ui';

const TABS = [['all', 'All entries'], ['ok', 'Succeeded'], ['fail', 'Failed']];

const statusOf = (a) => (a.success ? 'ok' : 'fail');
const searchFields = (a) => [a.action_type, a.lambda_function, a.error_message, a.output_summary];

export default function AuditLogPage() {
  const [showToast, toastNode] = useToast();
  const [items, setItems] = useState([]);
  const [date, setDate] = useState(todayEastern());
  const [loading, setLoading] = useState(true);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [tab, setTab] = useState('all');
  const [q, setQ] = useState('');
  const { sort, onSort, apply } = useSort('timestamp', 'desc');

  const load = useCallback(() => {
    setLoading(true);
    return api.getAuditLog(`?date=${date}`)
      .then(r => { setItems(r.items || []); setLastUpdated(new Date()); })
      .catch(() => showToast('Could not load the audit log', 'error'))
      .finally(() => setLoading(false));
  }, [date, showToast]);

  useEffect(() => { load(); }, [load]);

  const { counts, filtered } = useListFilter(items, { statusOf, searchFields, tab, q });
  const sorted = apply(filtered, (a, col) => {
    if (col === 'status') return statusOf(a);
    if (col === 'duration_ms') return Number(a.duration_ms) || 0;
    return a[col] || '';
  });
  const { pageRows, pagerProps } = usePager(sorted, [date, tab, q, sort.col, sort.dir]);

  return (
    <>
      <PageHeader
        title="Audit Log"
        subtitle="Every action the marketing agent took, one day at a time."
        meta={lastUpdated ? `Updated ${lastUpdated.toLocaleTimeString()}` : null}
        actions={(
          <>
            <input type="date" className="ops-input" value={date} max={todayEastern()} aria-label="Log date"
              onChange={e => { if (e.target.value) setDate(e.target.value); }} style={{ width: 160 }} />
            <button type="button" className="ops-btn" onClick={load} title="Refresh"><Icon name="refresh" /> Refresh</button>
          </>
        )}
      />

      {!loading && items.length > 0 && (
        <div className="ops-kpis">
          <StatCard label="Entries" value={counts.all} sub={date} />
          <StatCard label="Succeeded" value={counts.ok || 0} tone="green" />
          <StatCard label="Failed" value={counts.fail || 0} tone={counts.fail > 0 ? 'red' : undefined} />
        </div>
      )}

      <Panel flush
        title={<Tabs tabs={TABS} active={tab} counts={counts} onChange={setTab} />}
        action={<div className="ops-search"><Icon name="search" /><input className="ops-input" value={q} onChange={e => setQ(e.target.value)} placeholder="Search the log" aria-label="Search the log" /></div>}
        foot={!loading && sorted.length > 0 ? <Pager {...pagerProps} label="entries" /> : null}>
        {loading ? <Skeleton rows={6} />
          : items.length === 0 ? <Empty title={`No log entries for ${date}`}>Pick another date to see earlier activity.</Empty>
            : sorted.length === 0 ? <Empty title="No matching entries">Nothing matches this search or filter.</Empty> : (
              <div className="ops-table-wrap">
                <table className="ops-table">
                  <thead>
                    <tr>
                      <SortTh col="timestamp" label="Time" sort={sort} onSort={onSort} />
                      <SortTh col="action_type" label="Action" sort={sort} onSort={onSort} />
                      <th>Function</th>
                      <SortTh col="status" label="Result" sort={sort} onSort={onSort} />
                      <SortTh col="duration_ms" label="Duration" sort={sort} onSort={onSort} />
                      <th>Detail</th>
                    </tr>
                  </thead>
                  <tbody>
                    {pageRows.map(a => (
                      <tr key={a.log_id}>
                        <td className="mono muted">{toEasternTime(a.timestamp)}</td>
                        <td className="ops-cell-main">{a.action_type}</td>
                        <td className="muted">{a.lambda_function?.replace('hca-', '')}</td>
                        <td><StatusPill status={a.success ? 'completed' : 'failed'} label={a.success ? 'OK' : 'Failed'} /></td>
                        <td className="mono muted">{a.duration_ms ? `${a.duration_ms} ms` : ''}</td>
                        <td className={a.error_message ? 'neg' : 'muted'} style={{ maxWidth: 420, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={a.error_message || a.output_summary}>
                          {a.error_message || a.output_summary || ''}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
      </Panel>
      {toastNode}
    </>
  );
}
